import type { ModelDefinition } from "@/models.js";

export const inclusionaiModels = [
	{
		id: "ling-3.0-flash",
		name: "Ling 3.0 Flash",
		description:
			"InclusionAI's native hybrid-reasoning MoE model (124B total, 5.1B active) with strong agentic and coding performance.",
		family: "inclusionai",
		releasedAt: new Date("2026-08-02"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "inclusionAI/Ling-3.0-flash",
				inputPrice: "0.06e-6",
				cachedInputPrice: "0.012e-6",
				outputPrice: "0.18e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				// Thinking is enabled by default via the vLLM chat template; only
				// disabling is exposed through the chat-template flag, so only
				// `none` is advertised as a supported reasoning effort.
				reasoning: true,
				reasoningEfforts: ["none"],
				chatTemplateThinkingKey: "enable_thinking",
				// Thinking is controlled solely via the chat-template flag, so
				// `reasoning_effort` must NOT be forwarded to the provider.
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"response_format",
					"tools",
					"tool_choice",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "novita",
				externalId: "inclusionai/ling-3.0-flash",
				inputPrice: "0.06e-6",
				cachedInputPrice: "0.012e-6",
				outputPrice: "0.18e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				// Novita backend ignores chat_template_kwargs.enable_thinking (verified
				// live 2026-08-10) — thinking is always on, no client control.
				reasoningEfforts: [],
				// Novita rejects response_format outright for this model
				// ("does not support feature: structured-outputs"), for both
				// json_object and json_schema.
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stop",
					"stream",
					"tools",
					"tool_choice",
				],
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "ling-3.1-flash",
		name: "Ling 3.1 Flash",
		description:
			"InclusionAI's hybrid-reasoning MoE model (560B total, 25B active) with tool calling and a 262K context window.",
		family: "inclusionai",
		free: true,
		// Free upstream; Novita frequently answers "server overload".
		stability: "unstable",
		releasedAt: new Date("2026-10-02"),
		providers: [
			{
				providerId: "novita",
				externalId: "inclusionai/ling-3.1-flash",
				inputPrice: "0",
				// Cache hits are reported only intermittently, so caching is left
				// undeclared; the rate would be 0 either way.
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 32768,
				streaming: true,
				reasoning: true,
				// `none` turns thinking off; every other tier is accepted but
				// reasons identically.
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: false,
				tools: true,
				// With thinking on, "required" returns the call as text inside
				// reasoning_content and no tool_calls.
				supportedToolChoices: ["auto", "none", "function"],
				supportedToolChoicesWithThinkingDisabled: ["required"],
				// Novita rejects response_format for this model, both json_object
				// and json_schema, and silently ignores `stop`.
				jsonOutput: false,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"stream",
					"reasoning_effort",
					"tools",
					"tool_choice",
				],
			},
		],
	},
	{
		id: "ling-3.0-flash-vl",
		name: "Ling 3.0 Flash VL",
		description:
			"InclusionAI's Ling 3.0 Flash MoE with native image and video understanding added on top of its language capabilities.",
		family: "inclusionai",
		releasedAt: new Date("2026-09-10"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "inclusionAI/Ling-3.0-flash-VL",
				inputPrice: "0.06e-6",
				cachedInputPrice: "0.012e-6",
				outputPrice: "0.18e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "fp16",
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high"],
				vision: true,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
] as const satisfies ModelDefinition[];
